'use client';

import {
  DocumentTextIcon,
  ExclamationCircleIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import type { CvFile, MatchColors } from '../screening.types';
import { MATCH_COLORS, formatFileSize } from '../screening.constants';

interface CandidateCardProps {
  file: CvFile;
  isExpanded?: boolean;
  onToggle?: () => void;
  onRemove?: () => void;
  showRemove?: boolean;
}

const getStatusLabel = (status: CvFile['status'], t: (key: string) => string): string => {
  switch (status) {
    case 'pending':
      return t('screening.card.status.pending');
    case 'processing':
      return t('screening.card.status.processing');
    case 'matching':
      return t('screening.card.status.matching');
    case 'completed':
      return t('screening.card.status.completed');
    case 'error':
      return t('screening.card.status.failed');
    default:
      return t('screening.card.status.unknown');
  }
};

const getInterviewStatusBadge = (interviewStatus: CvFile['interviewStatus'], t: (key: string) => string) => {
  if (!interviewStatus) return null;

  const statusLower = interviewStatus.toLowerCase();

  if (statusLower === 'queued' || statusLower === 'interview_queued') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
        <ClockIcon className="size-3" />
        {t('screening.interview.queued')}
      </span>
    );
  }

  if (statusLower === 'in_progress' || statusLower === 'interview_in_progress') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
        <ChatBubbleLeftRightIcon className="size-3" />
        {t('screening.interview.in.progress')}
      </span>
    );
  }

  if (statusLower === 'completed' || statusLower === 'interview_completed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        <CheckCircleIcon className="size-3" />
        {t('screening.interview.complete')}
      </span>
    );
  }

  if (statusLower === 'scheduled' || statusLower === 'interview_scheduled') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
        <ChatBubbleLeftRightIcon className="size-3" />
        {t('screening.interview.scheduled')}
      </span>
    );
  }

  if (statusLower.includes('failed') || statusLower.includes('skipped')) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
        <ExclamationCircleIcon className="size-3" />
        {t('screening.interview.failed')}
      </span>
    );
  }

  return null;
};

const getIconBgClass = (isProcessing: boolean, isError: boolean, colors: MatchColors | null): string => {
  if (isProcessing) return 'bg-slate-100';
  if (isError) return 'bg-red-100';
  if (colors) return colors.bg;
  return 'bg-slate-100';
};

export const CandidateCard = ({
  file,
  isExpanded = false,
  onToggle,
  onRemove,
  showRemove = false,
}: CandidateCardProps) => {
  const { t } = useTranslation();
  const isProcessing = ['pending', 'processing', 'matching'].includes(file.status);
  const isCompleted = file.status === 'completed';
  const isError = file.status === 'error';
  const colors: MatchColors | null = file.matchLevel ? MATCH_COLORS[file.matchLevel] : null;

  return (
    <div
      className={`group relative overflow-hidden rounded-xl border bg-white p-4 shadow-sm transition-all ${
        isCompleted && colors ? `${colors.border} ${colors.bg}` : 'border-slate-200'
      } ${isError ? 'border-red-200 bg-red-50/50' : ''} ${
        !isError && onToggle ? 'cursor-pointer hover:shadow-md' : ''
      }`}
      onClick={!isError && onToggle ? onToggle : undefined}
      onKeyDown={!isError && onToggle ? (e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(); } : undefined}
      role={!isError && onToggle ? 'button' : undefined}
      tabIndex={!isError && onToggle ? 0 : undefined}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${getIconBgClass(isProcessing, isError, colors)}`}
        >
          {isProcessing && (
            <ArrowPathIcon className="size-5 animate-spin text-slate-400" />
          )}
          {isError && (
            <ExclamationCircleIcon className="size-5 text-red-500" />
          )}
          {!isProcessing && !isError && file.displayName && (
            <UserCircleIcon className={`size-5 ${colors?.icon || 'text-slate-500'}`} />
          )}
          {!isProcessing && !isError && !file.displayName && (
            <DocumentTextIcon className={`size-5 ${colors?.icon || 'text-slate-500'}`} />
          )}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          {/* Name/Title */}
          <h4 className="truncate font-semibold text-slate-800">
            {file.displayName || file.name}
          </h4>

          {/* Subtitle */}
          {file.headline ? (
            <p className="truncate text-sm text-slate-500">{file.headline}</p>
          ) : (
            <p className="text-xs text-slate-400">
              {formatFileSize(file.size)}
            </p>
          )}

          {/* Status */}
          {isProcessing && (
            <div className="mt-2 flex items-center gap-2">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full w-1/2 animate-pulse rounded-full bg-secondary" />
              </div>
              <span className="text-xs font-medium text-slate-500">
                {getStatusLabel(file.status, t)}
              </span>
            </div>
          )}

          {/* Error message */}
          {isError && (
            <p className="mt-1 text-xs font-medium text-red-500">
              {t('screening.failed.cv.processing')}
            </p>
          )}

          {/* Score Badges */}
          {isCompleted && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {file.score != null && colors && (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${colors.badge} bg-opacity-10`}>
                  {t('screening.score.match.cv')}
                  {': '}
                  {file.score}
                  {'%'}
                </span>
              )}
              {file.interviewScore != null && colors && (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${colors.badge} bg-opacity-10`}>
                  {t('screening.score.interview')}
                  {': '}
                  {file.interviewScore}
                  {'%'}
                </span>
              )}
              {file.finalScore != null && colors && (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${colors.badge} bg-opacity-10`}>
                  {t('screening.score.final')}
                  {': '}
                  {file.finalScore}
                  {'%'}
                </span>
              )}
            </div>
          )}

          {/* Interview Status Badge */}
          {isCompleted && file.interviewStatus && (
            <div className="mt-2">
              {getInterviewStatusBadge(file.interviewStatus, t)}
            </div>
          )}

          {/* Quick Contact Info (always visible when completed) */}
          {isCompleted && (file.email || file.phone) && (
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
              {file.email && (
                <span className="flex items-center gap-1">
                  <EnvelopeIcon className="size-3" />
                  <span className="truncate">{file.email}</span>
                </span>
              )}
              {file.phone && (
                <span className="flex items-center gap-1">
                  <PhoneIcon className="size-3" />
                  {file.phone}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && isCompleted && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          {/* Insights */}
          {file.insights && file.insights.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {t('screening.key.insights')}
              </p>
              <ul className="list-inside list-disc space-y-0.5 text-sm text-slate-600">
                {file.insights.slice(0, 3).map((insight) => (
                  <li key={`${file.id}-insight-${insight.substring(0, 20)}`}>{insight}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Interview Summary */}
          {file.interviewSummary && (
            <div className="mt-3 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {t('screening.interview.summary')}
              </p>
              <p className="text-sm text-slate-600">{file.interviewSummary}</p>
            </div>
          )}
        </div>
      )}

      {/* Remove button */}
      {showRemove && onRemove && (
        <button
          type="button"
          aria-label={t('global.action.remove')}
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
          className="absolute right-2 top-2 rounded-full p-1 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-600 group-hover:opacity-100"
        >
          <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default CandidateCard;
